/* -------------------------- Design imports start -------------------------- */
import { Box, Skeleton, Typography } from '@mui/material'
import ImageEditorToolbox from './ImageEditorToolbox'
import ImageEditorWorkspace from './ImageEditorWorkspace'
/* --------------------------- Design imports end --------------------------- */

/* ------------------------ Functional imports start ------------------------ */
import React, { useCallback, useEffect, useRef, useState } from 'react'
import type {
  ImageEditorExportSettings,
  TImageEditorContext,
  TImageEditorImage,
  TImageEditorShapes,
} from './utils/types'
import type { ReactElement } from 'react'
import { ImageEditorContext } from './ImageEditorContext'
import type Konva from 'konva'
import useUniqueKey from '../../hooks/useUniqueKey'
import LogTool from '../../logger/logTools'
import ActionHistory from '../../utils/ActionHistory'
import { toast } from 'react-toastify'
import { FileInfo } from '../../utils/types'
import ConfirmImageExportModal from './ConfirmImageExportModal'
import { getFileExtension, getFileName } from '../../features/Filesystem'
import { t } from 'i18next'
/* ------------------------- Functional imports end ------------------------- */

export const EDITOR_SUPPORTED_IMAGE_FORMATS = ['.jpg', '.jpeg', '.png']

interface Props {
  file?: FileInfo & { blob?: Blob } // if a blob is provided, the file / image has been edited before
  onExportImageAvailable: (filename: string, exportedImage: Blob) => void
  triggerExportImage: number | undefined
}

/* -------------------------------------------------------------------------- */
/*                               Start Component                              */
/* -------------------------------------------------------------------------- */
/**
 * An image manipulation component that allows users to edit images within their
 * browser.
 *
 * How to use:
 *  - Place the component anywhere in your application
 *  - provide an image source (url) - the image is downloaded automatically
 *  - provide an export callback (onExportImage) - this will be triggered whenever
 *    the triggerExportImage prop is set to a value that evaluates as Boolean(true)
 *  - provide a trigger export state (triggerExportImage) - every time this state
 *    is set to a truethy value a new export will be triggered
 * @param props
 * @constructor
 */
export default function ImageEditor(props: Props): ReactElement {
  /* ------------------------- Non state data start ------------------------- */
  const { file, onExportImageAvailable, triggerExportImage } = props
  const imgSrc: string | undefined = file?.document || undefined
  const log = new LogTool({ context: 'ImageEditor', logLevel: 'debug', enable: true })
  const uniqueShapeKey = useUniqueKey('shape')
  /* -------------------------- Non state data end -------------------------- */

  /* --------------------------- Flag states start -------------------------- */
  const [openExportModal, setOpenExportModal] = useState(false)
  /* ---------------------------- Flag states end --------------------------- */

  /* --------------------------- Data states start -------------------------- */
  const [image, setImage] = useState<TImageEditorImage>({
    url: imgSrc ?? '',
    bitmap: undefined,
  })
  const [shapes, setShapes] = useState<TImageEditorShapes>({})
  const [selectedShapeIds, setSelectedShapeIds] = useState<string[]>([])
  const [previewMode, changePreviewMode] = useState<'solid' | 'xray' | 'outline'>('solid')
  const [actionHistory] = useState<ActionHistory>(() => new ActionHistory({ maxHistoryLength: 15 }))
  /* ---------------------------- Data states end --------------------------- */

  /* ------------------------- Function states start ------------------------ */
  // The function that enables exporting the canvas. Note that this function is
  // set through and within the ImageEditorWorkspace component.
  const exportCanvas = useRef<((exportSettings: ImageEditorExportSettings) => void) | undefined>(
    undefined
  )
  /* -------------------------- Function states end ------------------------- */

  /* ------------------------ Utility functions start ----------------------- */
  /**
   * Do not use directly!
   * Use the createRectangle, createCircle, ... functions instead.
   *
   * Correctly inserts the specified Konva shape configuration into the shapes-state
   *
   * @param shape Any Konva shape configuration.
   */
  function _createShape(shape: Konva.ShapeConfig): void {
    // check if the provided config contains an id
    let id: string
    if ('id' in shape) id = shape.id as string
    else id = uniqueShapeKey.get()

    // insert new shape
    setShapes(prev => {
      if (id in prev) {
        throw new Error(
          'A shape requires a unique id of type string. Make sure to either ' +
            'provide a unique id or remove the id property from your shape configuration ' +
            'to let _createShape generate a unique id for you.'
        )
      }
      return { ...prev, [id]: { ...shape, id } }
    })

    // select the newly created shape
    setSelectedShapeIds([id])

    // enable undo and redo actions
    actionHistory.push({
      undo: {
        fn: deleteShapes,
        args: [[id]],
      },
      redo: {
        fn: _createShape,
        args: [{ ...shape, id }],
      },
    })
  }
  /**
   * Creates a new Konva Rectangle config and adds it to the shapes-state.
   * @param rectangle The Konva rectangle configuration to build the rectangle from.
   */
  function createRectangle(rectangle: Konva.RectConfig): void {
    _createShape({ ...rectangle, shapeType: 'Rectangle' })
  }
  /**
   * Creates a new Konva Circle config and adds it to the shapes-state.
   * @param circle The Konva circle configuration to build the circle from.
   */
  function createCircle(circle: Konva.CircleConfig): void {
    _createShape({ ...circle, shapeType: 'Circle' })
  }
  /**
   * Creates a new Konva Ellipse config and adds it to the shapes-state.
   * @param ellipse The Konva ellipse configuration to build the ellipse from.
   */
  function createEllipse(ellipse: Konva.EllipseConfig): void {
    _createShape({ ...ellipse, shapeType: 'Ellipse' })
  }

  /**
   * Updates any property of a shape. E.g.: position, rotation, color, ...
   * @param shapeId Id of the shape to update.
   * @param update Partial shape configuration to update the shape with.
   */
  function updateShape(shapeId: string, update: Partial<Konva.ShapeConfig>): void {
    // cache the specified shape before the update to enable undo/redo actions
    // Note: it is important to create a copy of the shape to make sure that it
    //  is not updated with the referenced shape configuration object
    const shapeCache = { ...shapes[shapeId] }
    // find the shape with the given id
    const shape = shapes[shapeId]
    if (!shape) {
      log.warn(`Shape with id ${shapeId} not found. Unable to update shape.`)
      return
    }

    // strip out any properties that are not allowed to be updated
    ;['id', 'shapeType'].forEach(prop => delete update[prop])

    // apply the update
    setShapes(prev => ({ ...prev, [shapeId]: { ...shape, ...update } }))

    // enable undo and redo actions
    actionHistory.push({
      undo: {
        fn: updateShape,
        args: [shapeId, shapeCache],
      },
      redo: {
        fn: updateShape,
        args: [shapeId, update],
      },
    })
  }

  /**
   * Deletes a single shape specified by the shapeId.
   * @param shapeIds Ids of the shapes to delete
   */
  function deleteShapes(shapeIds: string[]): void {
    if (shapeIds.length === 0) return
    // cache the selected shapes to enable undo/redo actions
    // Note: it is important to create a copy of the shapes to make sure that they
    //  are not deleted with the referenced shape configuration objects
    const selectedShapesCache = shapeIds.map(shapeId => shapes[shapeId])

    // delete shapes
    setShapes(prev => {
      const newShapes = { ...prev }
      shapeIds.forEach(shapeId => {
        delete newShapes[shapeId]
      })
      return newShapes
    })

    // remove deleted shapes from selection
    setSelectedShapeIds(prev => prev.filter(shapeId => !shapeIds.includes(shapeId)))

    // enable undo and redo actions
    actionHistory.push({
      undo: {
        fn: function recreateShapes(shapes: Konva.ShapeConfig[]) {
          shapes.forEach(_createShape)
        },
        args: [selectedShapesCache],
      },
      redo: {
        fn: deleteShapes,
        args: [shapeIds],
      },
    })
  }

  /* ------------------------- Utility functions end ------------------------ */

  /* ----------------------- Callback functions start ----------------------- */
  const handleUndo = useCallback((): void => {
    try {
      actionHistory.undo()
    } catch (e) {
      toast.warn('Reached the end of the undo history.')
      log.error('Error executing undo ->', e)
    }
  }, [actionHistory])
  const handleRedo = useCallback((): void => {
    try {
      actionHistory.redo()
    } catch (e) {
      toast.warn('Reached the end of the redo history.')
      log.error('Error executing redo ->', e)
    }
  }, [actionHistory])
  const handleCtrlZY = useCallback(
    (e: any): void => {
      if (e.ctrlKey && e.key === 'z') handleUndo()
      else if (e.ctrlKey && e.key === 'y') handleRedo()
    },
    [handleUndo, handleRedo]
  )
  /* ------------------------ Callback functions end ------------------------ */

  /* ----------------------------- Effects start ---------------------------- */
  // load the image, whenever the file prop changes. This tries to load the image
  // from file.blob if available, otherwise downloads the image using file.document
  useEffect(() => {
    // the file object has changed. That could mean one of two things:
    // 1. We received a new file object pointing to a new image -> download image using file.document
    // 2. We received a new file object pointing to an image that has been edited before
    //    -> load the image using file.blob
    const loadFromBlob = (blob: Blob) => {
      window
        .createImageBitmap(blob)
        .then((bitmapFromBlob: ImageBitmap) => {
          setImage(prev => {
            return {
              ...prev,
              bitmap: bitmapFromBlob,
            }
          })
          return
        })
        .catch(reason => {
          log.error('Error loading file from Blob: ', reason)
          throw new Error('Error loading file from Blob. Make sure to catch this.')
        })
    }
    const loadFromUrl = (url: string) => {
      const imgEl = new window.Image()
      // this makes sure we do not get CORS errors when exporting the image
      imgEl.crossOrigin = 'Anonymous'
      imgEl.src = url
      imgEl.onload = async () => {
        const bitmap = await window.createImageBitmap(imgEl)
        setImage({
          url: url,
          bitmap: bitmap,
          width: bitmap.width,
          height: bitmap.height,
        })
      }
    }

    if (image.bitmap) {
      // Remove the previous image from vram before loading a different image.
      // This prevents memory leaks and optimizes performance.
      image.bitmap.close()
    }

    if (Object.hasOwn(file as FileInfo, 'blob')) {
      // the image has been edited before -> load the edited version of the image
      // to enable re-editing the file
      try {
        loadFromBlob(file!.blob!)
      } catch {
        // fallback to downloading the image using file.document
        if (typeof file?.document === 'string' && file.document !== '') {
          loadFromUrl(file.document)
        }
      }
    } else {
      // we do not have the files content available -> download the image
      if (typeof file?.document === 'string' && file.document !== '') {
        // we have a (new) image url -> download the image and generate a bitmap
        loadFromUrl(file.document)
      }
    }

    // cleanup
    return () => {
      if (image.bitmap) {
        // remove image from vram on un-mount. This prevents memory leaks and frees vram
        image.bitmap.close()
      }
    }
  }, [file])

  // register key press event listener on component mount
  useEffect(() => {
    // registering the event listener on the window object ensures that we can
    // capture the key event regardless of which element has focus
    window.addEventListener('keydown', handleCtrlZY)
    //remove event listener on unmount
    return () => {
      window.removeEventListener('keydown', handleCtrlZY)
    }
  }, [handleCtrlZY])

  // call exportImage function whenever the triggerExportImage state changes to true
  useEffect(() => {
    if (triggerExportImage) {
      if (typeof exportCanvas.current === 'function') {
        // open the export openExportModal
        setOpenExportModal(true)
      } else {
        toast.warn('Image export is not available yet. Retry in a few seconds.')
      }
    }
  }, [triggerExportImage])
  /* ------------------------------ Effects end ----------------------------- */

  /* ----------------------- Pre render actions start ----------------------- */

  // collect states and functions into one image editor context object
  const imageEditorContext: TImageEditorContext = {
    image,
    shapes,
    shapeSelection: { ids: selectedShapeIds, setIds: setSelectedShapeIds },
    createRectangle,
    createCircle,
    createEllipse,
    updateShape,
    deleteShapes,
    undo: handleUndo,
    redo: handleRedo,
    previewSettings: {
      mode: previewMode,
      changeMode: changePreviewMode,
    },
    exportCanvas,
  }
  /* ------------------------ Pre render actions end ------------------------ */

  log.debug('shapes ->', shapes, 'actionHistory ->', actionHistory)

  /* ------------------------------------------------------------------------ */
  /*                             Render component                             */
  /* ------------------------------------------------------------------------ */
  return (
    <>
      <ImageEditorContext.Provider value={imageEditorContext}>
        <Box
          id="image-editor-container"
          height="100%"
          width="100%"
          display="flex"
          flexDirection="column"
        >
          <Box id="image-editor-toolbox" mb="16px">
            <ImageEditorToolbox disabled={!image.bitmap} />
          </Box>
          <Box id="image-editor-workspace" flexGrow={1}>
            {image.url ? (
              image.bitmap ? (
                <ImageEditorWorkspace />
              ) : (
                <Skeleton variant="rounded" width="100%" height="100%" />
              )
            ) : (
              <Box display="flex" alignItems="center" justifyContent="center">
                <Typography variant="body2" color="lightgray">
                  No image available
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </ImageEditorContext.Provider>
      <ConfirmImageExportModal
        open={openExportModal}
        setOpen={setOpenExportModal}
        filename={getFileName(file?.name ?? '')}
        confirmButtonProps={{
          text: t('imageManipulation:interaction.button.exportImage'),
          onConfirm: (filename: string, quality: number) => {
            if (!exportCanvas.current) return
            exportCanvas.current({
              // if the exported image is available as Blob, we reach it up to The
              // component, that triggered the export, together with the filename
              // that was specified in the ConfirmImageExportModal
              onImageAvailable: (exportImage: Blob) =>
                onExportImageAvailable(filename, exportImage),
              image: image,
              shapes: Object.values(shapes),
              quality: quality < 0 ? 0 : quality > 1 ? 1 : quality,
            })
          },
          disabled: false,
        }}
      />
    </>
  )
}
